<script setup>
import { Form } from "vee-validate";
import IntlTelInput from "intl-tel-input/vueWithUtils";
import "intl-tel-input/styles";
import { array, object, string, ref as yupRef } from "yup";
import { FORM_ERROR_MESSAGES } from "@/validation/index.js";
import { useOrganizationStore } from "@/stores/organization.js";
import { useUserStore } from "@/stores/user";
import { ROLE_PERMISSIONS_TOOLTIP } from "@/constants/verbiage.js";

const emit = defineEmits(["update:model-value", "create-user"]);
const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  rolePermissionsOptions: {
    type: Array,
    default: () => [],
  },
  accessStatusOptions: {
    type: Array,
    default: () => [],
  },
  departmentOptions: {
    type: Array,
    default: () => [],
  },
  unitsOptions: {
    type: Array,
    default: () => [],
  },
  unitInitialValue: {
    type: String,
    default: "",
  },
  initialPrimaryEmailAddress: {
    type: String,
    default: "",
  },
  initialRoleId: {
    type: String,
    default: "",
  },
  enableInviteUser: {
    type: Boolean,
    default: true,
  },
  showChannels: {
    type: Boolean,
    default: true,
  },
});
const initialFormValues = {
  first_name: "",
  last_name: "",
  role_ids: null,
  unit_ids: [],
  title: "",
  department_ids: [],
  primary_email: "",
  secondary_email: "",
  primary_phone_number: "",
  is_primary_phone_number_valid: false,
  secondary_phone_number: "",
  is_secondary_phone_number_valid: false,
  slack_id: "",
  msteams_id: "",
  meta_id: "",
  wordpress_id: "",
  alc_tags: [],
};

const createUserForm = ref(structuredClone(initialFormValues));
const formRef = ref(null);

const handleSubmit = async (sendInvite) => {
  const isValid = await formRef.value.validate();
  if (!isValid) {
    return;
  }
  // since the phone numbers are not part of the vee-validate logic, validate
  // since the phone numbers are not required, an empty value is valid
  const isPhoneValid = (
    createUserForm.value.primary_phone_number
      && createUserForm.value.is_primary_phone_number_valid
      || !createUserForm.value.primary_phone_number
  );
  const isSecondaryPhoneValid = (
    createUserForm.value.secondary_phone_number
      && createUserForm.value.is_secondary_phone_number_valid
      || !createUserForm.value.secondary_phone_number
  );
  if(!isPhoneValid || !isSecondaryPhoneValid){
    return
  }
  const payload = {
    ...createUserForm.value,
    role_ids: createUserForm.value.role_ids
      ? [createUserForm.value.role_ids?.value || createUserForm.value.role_ids]
      : [],
    status: "SAVED",
    sendInvite, 
  };

  emit("create-user", payload);
};

const userStore = useUserStore();

const jobTitles = computed(() => {
  const titles = new Set();
  for(let user of Object.values(userStore.allDict)){
    if(user.title){
      titles.add(user.title)
    }
  }
  return [...titles].sort(); 
});

const organizationStore = useOrganizationStore();
const groupedInputs = computed(() => {
  const channels = organizationStore.myOrganization?.access_channels || [];
  const access_channel_inputs = [
    {
      label: "Slack",
      name: "slack_id",
      condition: channels.includes("slack"),
    },
    {
      label: "Microsoft Teams ID",
      name: "msteams_id",
      condition: channels.includes("msteams"),
    },
    {
      label: "Meta ID",
      name: "meta_id",
      condition: channels.includes("meta"),
    },
    {
      label: "Wordpress ID",
      name: "wordpress_id",
      condition: channels.includes("wordpress"),
    },
    {
      label: "Workvivo ID",
      name: "workvivo_id",
      condition: channels.includes("workvivo"),
    },
  ];

  const visibleInputs = access_channel_inputs.filter(
    (input) => input.condition
  );

  const rows = [];
  for (let i = 0; i < visibleInputs.length; i += 2) {
    rows.push(visibleInputs.slice(i, i + 2));
  }

  return rows;
});

function onChangePrimaryNumber(data){
    createUserForm.value.primary_phone_number = data;
}
function onChangePrimaryValidity(isValid){
  console.log(isValid, createUserForm.value.primary_phone_number);
  
  if((!isValid && createUserForm.value.primary_phone_number) || isValid){
    createUserForm.value.is_primary_phone_number_valid = isValid;
  }
}


function onChangeSecondaryNumber(data){
  createUserForm.value.secondary_phone_number = data;
}
function onChangeSecondaryValidity(isValid){
  console.log(isValid, createUserForm.value.secondary_phone_number);
  if((!isValid && createUserForm.value.secondary_phone_number) || isValid){
    createUserForm.value.is_secondary_phone_number_valid = isValid;
  }
}

const createUserSchema = object({
  first_name: string(FORM_ERROR_MESSAGES.STRING).required(
    FORM_ERROR_MESSAGES.REQUIRED
  ),
  last_name: string(FORM_ERROR_MESSAGES.STRING).required(
      FORM_ERROR_MESSAGES.REQUIRED
  ),
  role_ids: object().required(
      FORM_ERROR_MESSAGES.REQUIRED
  ),
  unit_ids: array().min(
    1,
    FORM_ERROR_MESSAGES.SHOULD_CONTAIN_AT_LEAST_ELEMENTS(1)
  ),
  title: string(FORM_ERROR_MESSAGES.STRING).required(
    FORM_ERROR_MESSAGES.REQUIRED
  ),
  department_ids: array(),
  primary_email: string()
    .email(FORM_ERROR_MESSAGES.EMAIL)
    .required(FORM_ERROR_MESSAGES.REQUIRED),
  secondary_email: string().email(FORM_ERROR_MESSAGES.EMAIL),
  slack_id: string(FORM_ERROR_MESSAGES.STRING),
  msteams_id: string(FORM_ERROR_MESSAGES.STRING),
  meta_id: string(FORM_ERROR_MESSAGES.STRING),
  wordpress_id: string(FORM_ERROR_MESSAGES.STRING),
});

watch(
  () => props.modelValue,
  async (val) => {
    if (!val) {
      await formRef.value.resetForm();
      createUserForm.value = structuredClone(initialFormValues);
    }
  }
);

watch(
  () => props.unitInitialValue,
  (val) => {
    if (val) {
      createUserForm.value.unit_ids.push(val);
    }
  },
  {
    deep: true,
  }
);

watch(
  () => props.initialPrimaryEmailAddress,
  (val) => {
    createUserForm.value.primary_email = val;
  },
  {
    immediate: true,
  }
);

watch(
  () => props.initialRoleId,
  (val) => {
    createUserForm.value.role_ids = parseInt(val);
  },
  {
    immediate: true,
  }
);

</script>

<template>
  <BModal
    hide-footer
    size="lg"
    title="New User"
    :model-value="modelValue"
    @update:model-value="(val) => $emit('update:model-value', val)"
  >
    <div>
      <Form
        ref="formRef"
        :validation-schema="createUserSchema"
        v-slot="{ errors }"
      >
        <div class="d-flex flex-column my-row-gap-4">
          <div class="d-flex column-gap-4">
            <TextInput
              label="First Name *"
              name="first_name"
              :has-bold-label="false"
              v-model="createUserForm.first_name"
            />
            <TextInput
              label="Last Name *"
              name="last_name"
              :has-bold-label="false"
              v-model="createUserForm.last_name"
            />
          </div>

          <div class="d-flex column-gap-4">
            <div class="w-100">
              <SelectInput
                name="unit_ids"
                multiple
                :options="unitsOptions"
                v-model="createUserForm.unit_ids"
                :reduce="(option) => option.value"
                :has-bold-label="false"
                label="Unit *"
              >
                <template #list-header>
                  <span
                    @click="$emit('addNewUnit', '')"
                    class="font-size-sm text-neutral-400 cursor-pointer"
                    style="padding: 8px"
                    >Add New</span
                  >
                </template>
              </SelectInput>
            </div>

            <div class="w-100">
              <TextInput
                label="Job Title *"
                name="title"
                :has-bold-label="false"
                v-model="createUserForm.title"
                autocomplete
                :autocomplete-options="jobTitles"
              />
            </div>
          </div>

          <div class="d-grid column-gap-4 grid-template-columns">
            <!-- <div class="w-100">
              <SelectInput
                name="department_ids"
                label="Department"
                multiple
                :options="departmentOptions"
                :has-bold-label="false"
                :reduce="(option) => option.value"
                v-model="createUserForm.department_ids"
              />
            </div> -->

            <div class="w-100">
              <SelectInput
                name="role_ids"
                :options="rolePermissionsOptions"
                v-model="createUserForm.role_ids"
                :has-bold-label="false"
                label="Role *"
              >
                <span> Role *</span>
                <span
                  class="ms-1"
                  v-b-tooltip.hover.top="ROLE_PERMISSIONS_TOOLTIP"
                >
                  <IconInfo />
                </span>
              </SelectInput>
            </div>

            <div
              class="w-100"
              v-if="userStore.hasPerm('edit-alc-tags')"
            >
              <TextInput
                label="Security Labels"
                name="security_labels"
                :has-bold-label="false"
                :model-value="createUserForm.alc_tags?.join(', ')"
                @update:model-value="
                  (val) => createUserForm.alc_tags = val.split(',').map(
                    (x) => x.trim()
                  ).filter(
                    (x) => x.length > 0
                  )
                "
              />
            </div>
          </div>
        </div>

        <div class="my-mt-8 d-flex flex-column my-row-gap-4">
          <div class="d-flex column-gap-4">
            <TextInput
              label="Primary Email Address *"
              name="primary_email"
              :has-bold-label="false"
              v-model="createUserForm.primary_email"
            />
            <TextInput
              label="Secondary Email Address"
              name="secondary_email"
              :has-bold-label="false"
              v-model="createUserForm.secondary_email"
            />
          </div>

          <div class="d-flex column-gap-4">
            
            <div class="">
              <label for="primaryPhoneInput" 
                  class="label p-0 text-left text-decoration-none text-neutral-350">
                  Primary Phone Number
              </label>
              <IntlTelInput
                :options="{
                  initialCountry: 'ca',
                  countryOrder:['ca', 'us'],
                }"
                :inputProps="{
                  id:'primaryPhoneInput',
                  style:'padding-left: 50px;',
                  class:'custom-input'
                }"
                @changeNumber="onChangePrimaryNumber"
                @changeValidity="onChangePrimaryValidity"
              /> <br/>
              <span class="input-error" v-if="createUserForm?.primary_phone_number && !createUserForm?.is_primary_phone_number_valid">Invalid Phone Number</span>
            </div>
            <div class="">
              <label for="secondaryPhoneInput" 
                  class="label p-0 text-left text-decoration-none text-neutral-350">
                  Secondary Phone Number
              </label>
              <IntlTelInput
                :options="{
                  initialCountry: 'ca',
                  countryOrder:['ca', 'us'],
                }"
                :inputProps="{
                  id:'secondaryPhoneInput',
                  style:'padding-left: 50px;',
                  class:'custom-input'
                }"
                @changeNumber="onChangeSecondaryNumber"
                @changeValidity="onChangeSecondaryValidity"
              /> <br/>
              <span class="input-error" v-if="createUserForm?.secondary_phone_number && !createUserForm?.is_secondary_phone_number_valid">Invalid Phone Number</span>
            </div>
          </div>

          <div
            v-if="showChannels"
          >
            <div
              class="d-grid column-gap-4 grid-template-columns"
              v-for="(row, rowIndex) in groupedInputs"
              :key="rowIndex"
            >
              <TextInput
                v-for="input in row"
                :key="input.name"
                :label="input.label"
                :name="input.name"
                :has-bold-label="false"
                v-model="createUserForm[input.name]"
              />
            </div>
          </div>
        </div>

        <div class="my-mt-4 d-flex justify-content-end">
          <CustomButton
            type="button"
            :button-type="enableInviteUser ? 'white' : 'blue'"
            has-font-semi-bold
            class="me-2"
            @click="handleSubmit(false)"
          >
            Create User
          </CustomButton>
          <CustomButton
            v-if="enableInviteUser"
            type="button"
            button-type="blue"
            has-font-semi-bold
            @click="handleSubmit(true)"
          >
            Create User & Invite
          </CustomButton>
        </div>
      </Form>
    </div>
  </BModal>
</template>

<style lang="scss" scoped>

.label {
  font-size: $font-size-sm;
  color: $neutral-350;
  margin-bottom: 0.5rem;
}
.input-error{
  display: inline-block;
  margin-bottom: 4px;
  margin-top: 0.650rem;
  color: #F8775D;
  font-size: 12px;
}
.custom-input {
  border: 1px solid $neutral-150;
  height: 2.375rem;
  font-size: $font-size-sm;

  &:focus-visible {
    outline: none;
    box-shadow: none;
  }

  &::placeholder {
    font-size: $font-size-sm;
    color: $neutral-300;
  }
}
</style>
